import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Brain, Music, BookOpen, Users, MessageCircle, ChevronDown, ChevronUp, ExternalLink, Play } from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1767460959466-9f2e6f44b5f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHx5b2dhJTIwemVuJTIwZ2FyZGVuJTIwY2FsbSUyMHNlcmVuZSUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3NzA1MTQ1MDR8MA&ixlib=rb-4.1.0&q=85';
const MEDITATION_IMG = 'https://images.unsplash.com/photo-1763723341417-a4ea5ba6f675?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHx3ZWxsbmVzcyUyMG1lZGl0YXRpb24lMjBtaW5kZnVsbmVzcyUyMHBlYWNlZnVsfGVufDB8fHx8MTc3MDUxNDQ5NHww&ixlib=rb-4.1.0&q=85';
const HAPPY_IMG = 'https://images.unsplash.com/photo-1612723203798-321285cf5e3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxlbW90aW9uYWwlMjB3ZWxsYmVpbmclMjBoYXBweSUyMHBlb3BsZSUyMG5hdHVyZXxlbnwwfHx8fDE3NzA1MTQ0OTl8MA&ixlib=rb-4.1.0&q=85';
const ZEN_IMG = 'https://images.unsplash.com/photo-1764054655220-99548fc12fe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHx3ZWxsbmVzcyUyMG1lZGl0YXRpb24lMjBtaW5kZnVsbmVzcyUyMHBlYWNlZnVsfGVufDB8fHx8MTc3MDUxNDQ5NHww&ixlib=rb-4.1.0&q=85';

const coaches = [
  { name: 'Dr. Brene Brown', focus: 'Vulnerability & Emotional Courage', url: 'https://brenebrown.com/', description: 'Research professor studying courage, vulnerability, shame, and empathy.' },
  { name: 'Jay Shetty', focus: 'Mindfulness & Purpose', url: 'https://jayshetty.me/', description: 'Former monk turned life coach, bestselling author of "Think Like a Monk".' },
  { name: 'Dr. Gabor Mate', focus: 'Trauma & Emotional Health', url: 'https://drgabormate.com/', description: 'World-renowned speaker on addiction, stress, and childhood development.' },
  { name: 'Tara Brach', focus: 'Meditation & Radical Acceptance', url: 'https://www.tarabrach.com/', description: 'Psychologist and meditation teacher, author of "Radical Acceptance".' },
  { name: 'Sadhguru', focus: 'Inner Engineering & Wellbeing', url: 'https://isha.sadhguru.org/', description: 'Yogi, mystic, and founder of Isha Foundation, advocating inner transformation.' },
  { name: 'Dr. Andrew Huberman', focus: 'Neuroscience & Mental Health', url: 'https://www.hubermanlab.com/', description: 'Stanford neuroscientist sharing science-based tools for everyday life.' },
];

const videos = [
  { id: 'iCvmsMzlF7o', title: 'The Power of Vulnerability', speaker: 'Brene Brown', source: 'TED' },
  { id: 'w6T02g5hnT4', title: 'How to Make Stress Your Friend', speaker: 'Kelly McGonigal', source: 'TED' },
  { id: 'LnJwH_PZXnM', title: 'Emotional First Aid', speaker: 'Guy Winch', source: 'TED' },
];

const useCases = [
  { icon: Heart, title: 'Daily Emotional Check-ins', desc: 'Start each day by identifying how you feel. Over time, patterns emerge that help you understand your triggers and rhythms.' },
  { icon: Brain, title: 'Therapy Companion', desc: 'Share your emotion logs with your therapist. Mojo helps bridge the gap between sessions with data-driven insights.' },
  { icon: Music, title: 'Mood-Based Music Healing', desc: 'Music is medicine. Get personalized song recommendations that match or gently shift your emotional state.' },
  { icon: MessageCircle, title: 'AI Wellness Companion', desc: 'When you need someone to talk to at 3 AM, Mojo is there — empathetic, judgment-free, and always available.' },
  { icon: BookOpen, title: 'Reflective Journaling', desc: 'Pair your mood entries with journal prompts to process feelings deeply and build emotional resilience.' },
  { icon: Users, title: 'Community Wellness', desc: 'See anonymized emotional trends in your area. Know you are not alone in what you feel.' },
];

function VideoCard({ video }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="rounded-2xl overflow-hidden bg-white shadow-sm border border-stone-200">
      <div className="relative aspect-video bg-stone-900">
        {playing ? (
          <iframe
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1`}
            title={video.title}
            allow="autoplay; encrypted-media"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        ) : (
          <button onClick={() => setPlaying(true)} className="absolute inset-0 w-full h-full group" data-testid={`play-video-${video.id}`}>
            <img src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`} alt={video.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Play className="w-6 h-6 text-hunter-green ml-1" />
              </div>
            </div>
          </button>
        )}
      </div>
      <div className="p-4">
        <p className="text-xs text-hunter-green font-semibold uppercase tracking-wider">{video.source}</p>
        <h4 className="font-semibold text-sm mt-1 text-warm-charcoal">{video.title}</h4>
        <p className="text-xs text-warm-charcoal/60 mt-0.5">{video.speaker}</p>
      </div>
    </div>
  );
}

function ExpandableSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-stone-200 rounded-2xl overflow-hidden bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-stone-50 transition-colors"
        data-testid={`section-toggle-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <h3 className="text-base font-semibold text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>{title}</h3>
        {open ? <ChevronUp className="w-5 h-5 text-warm-charcoal/50" /> : <ChevronDown className="w-5 h-5 text-warm-charcoal/50" />}
      </button>
      {open && <div className="px-5 pb-5 pt-0">{children}</div>}
    </div>
  );
}

export default function AboutUs({ showAuthButton = false }) {
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem('mojo_token');

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-stone-50 to-stone-100">
      {/* Sticky Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-stone-200/50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-stone-100 transition-colors" data-testid="about-back-btn">
            <ArrowLeft className="w-5 h-5 text-warm-charcoal" />
          </button>
          <h1 className="text-lg font-semibold text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>About Mind My Mojo</h1>
          <div className="w-9" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 pb-20">
        {/* Hero */}
        <section className="relative rounded-3xl overflow-hidden mt-6 mb-10" data-testid="about-hero">
          <img src={HERO_IMG} alt="Serene wellness landscape" className="w-full h-64 md:h-80 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex items-end">
            <div className="p-6 md:p-10">
              <h2 className="text-2xl md:text-4xl font-bold text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Your Emotional Wellness,<br />Reimagined
              </h2>
              <p className="text-sm md:text-base text-white/80 mt-2 max-w-xl">
                Mind My Mojo is a science-backed emotional health platform that helps you understand, track, and nurture your inner world.
              </p>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="grid md:grid-cols-2 gap-8 items-center mb-12" data-testid="about-mission">
          <div>
            <p className="text-xs font-semibold text-hunter-green uppercase tracking-widest mb-2">Our Mission</p>
            <h3 className="text-xl md:text-2xl font-semibold text-warm-charcoal mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Making Emotional Intelligence Accessible to Everyone
            </h3>
            <p className="text-sm text-warm-charcoal/70 leading-relaxed mb-3">
              We believe that understanding your emotions is not a luxury — it is a fundamental life skill. Too often, people are told to "just be positive" without the tools to actually understand what they feel and why.
            </p>
            <p className="text-sm text-warm-charcoal/70 leading-relaxed">
              Mind My Mojo brings together decades of psychological research — from Paul Ekman's basic emotions to Robert Plutchik's Wheel of Emotions and Bob Hoffman's nuanced feeling categories — into a beautiful, intuitive app that fits in your pocket.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <img src={MEDITATION_IMG} alt="Mindful meditation" className="w-full h-64 object-cover" />
          </div>
        </section>

        {/* How It Works */}
        <section className="mb-12" data-testid="about-how-it-works">
          <p className="text-xs font-semibold text-hunter-green uppercase tracking-widest mb-2 text-center">How It Works</p>
          <h3 className="text-xl md:text-2xl font-semibold text-warm-charcoal mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            Three Steps to Emotional Clarity
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: '01', title: 'Check In', desc: 'Select a primary emotion category, then pinpoint your specific feeling from a curated list based on the Hoffman Process.', img: ZEN_IMG },
              { step: '02', title: 'Reflect', desc: 'Journal about your feelings, chat with our AI companion Mojo, or listen to mood-matched music to process your emotions.', img: HAPPY_IMG },
              { step: '03', title: 'Grow', desc: 'Track patterns over time, earn wellness badges, and build emotional resilience with evidence-based recommendations.', img: HERO_IMG },
            ].map((s) => (
              <div key={s.step} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-stone-200 hover:shadow-md transition-shadow">
                <img src={s.img} alt={s.title} className="w-full h-36 object-cover" />
                <div className="p-5">
                  <span className="text-3xl font-bold text-hunter-green/20">{s.step}</span>
                  <h4 className="text-base font-semibold text-warm-charcoal mt-1">{s.title}</h4>
                  <p className="text-xs text-warm-charcoal/60 mt-2 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Use Cases */}
        <section className="mb-12" data-testid="about-use-cases">
          <p className="text-xs font-semibold text-hunter-green uppercase tracking-widest mb-2 text-center">Use Cases</p>
          <h3 className="text-xl md:text-2xl font-semibold text-warm-charcoal mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            How People Use Mind My Mojo
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {useCases.map((uc) => {
              const Icon = uc.icon;
              return (
                <div key={uc.title} className="bg-white rounded-2xl p-5 border border-stone-200 hover:border-hunter-green/30 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-hunter-green/10 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-hunter-green" />
                  </div>
                  <h4 className="font-semibold text-sm text-warm-charcoal">{uc.title}</h4>
                  <p className="text-xs text-warm-charcoal/60 mt-1.5 leading-relaxed">{uc.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Science Section - Expandable */}
        <section className="mb-12 space-y-3" data-testid="about-science">
          <p className="text-xs font-semibold text-hunter-green uppercase tracking-widest mb-2 text-center">The Science</p>
          <h3 className="text-xl md:text-2xl font-semibold text-warm-charcoal mb-6 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            Grounded in Research, Built with Care
          </h3>
          <ExpandableSection title="The Hoffman Feeling Categories" defaultOpen={true}>
            <p className="text-sm text-warm-charcoal/70 leading-relaxed">
              Developed as part of the Hoffman Process, these 18 nuanced emotional categories go far beyond "happy" or "sad." They include states like <strong>Fragile</strong>, <strong>Disconnected/Numb</strong>, <strong>Courageous/Powerful</strong>, and <strong>Unsettled/Doubt</strong> — feelings that are common but rarely named. By giving precise language to what you feel, you gain power over it. Research shows that the act of labeling emotions (called "affect labeling") reduces amygdala activity and helps regulate emotional responses.
            </p>
          </ExpandableSection>
          <ExpandableSection title="Plutchik's Wheel of Emotions">
            <p className="text-sm text-warm-charcoal/70 leading-relaxed">
              Robert Plutchik's psycho-evolutionary theory identifies eight primary emotions arranged in opposing pairs: joy-sadness, trust-disgust, fear-anger, surprise-anticipation. These emotions can combine to form complex feelings — like love (joy + trust) or awe (surprise + fear). Mind My Mojo uses this framework to help users explore the layered nature of their emotional experiences.
            </p>
          </ExpandableSection>
          <ExpandableSection title="Music Therapy & Emotional Regulation">
            <p className="text-sm text-warm-charcoal/70 leading-relaxed">
              The therapeutic use of music to address emotional needs is backed by extensive research. Studies published in the <em>Journal of Music Therapy</em> show that listening to music matching one's current emotional state (the "iso principle") followed by gradually shifting the music can effectively regulate mood. Mind My Mojo's mood-based music recommendations are inspired by this evidence-based approach, providing songs across genres, eras, and languages to meet you where you are.
            </p>
          </ExpandableSection>
        </section>

        {/* Wellness Videos */}
        <section className="mb-12" data-testid="about-videos">
          <p className="text-xs font-semibold text-hunter-green uppercase tracking-widest mb-2 text-center">Watch & Learn</p>
          <h3 className="text-xl md:text-2xl font-semibold text-warm-charcoal mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            Talks That Inspire Emotional Growth
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {videos.map((v) => <VideoCard key={v.id} video={v} />)}
          </div>
        </section>

        {/* Renowned Coaches */}
        <section className="mb-12" data-testid="about-coaches">
          <p className="text-xs font-semibold text-hunter-green uppercase tracking-widest mb-2 text-center">Learn from the Best</p>
          <h3 className="text-xl md:text-2xl font-semibold text-warm-charcoal mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            Renowned Wellness & Wellbeing Coaches
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {coaches.map((c) => (
              <a
                key={c.name}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white rounded-2xl p-5 border border-stone-200 hover:border-hunter-green/40 hover:shadow-md transition-all group"
                data-testid={`coach-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-sm text-warm-charcoal group-hover:text-hunter-green transition-colors">{c.name}</h4>
                    <p className="text-xs text-hunter-green font-medium mt-0.5">{c.focus}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-warm-charcoal/30 group-hover:text-hunter-green transition-colors flex-shrink-0 mt-1" />
                </div>
                <p className="text-xs text-warm-charcoal/60 mt-2 leading-relaxed">{c.description}</p>
              </a>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-10 mb-10" data-testid="about-cta">
          <h3 className="text-xl md:text-2xl font-semibold text-warm-charcoal mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Understand Your Inner World?
          </h3>
          <p className="text-sm text-warm-charcoal/60 mb-6 max-w-md mx-auto">
            Join thousands who are building emotional intelligence, one check-in at a time.
          </p>
          <button
            onClick={() => navigate(isLoggedIn ? '/dashboard' : '/')}
            className="px-8 py-3 rounded-full bg-hunter-green text-white font-medium text-sm hover:bg-hunter-green/90 transition-colors shadow-lg"
            data-testid="about-cta-button"
          >
            {isLoggedIn ? 'Go to Dashboard' : 'Get Started Free'}
          </button>
        </section>
      </main>

      {/* Floating Sign In / Sign Up button for non-logged-in visitors */}
      {showAuthButton && !isLoggedIn && (
        <button
          onClick={() => navigate('/login')}
          data-testid="floating-auth-btn"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-hunter-green/90 backdrop-blur-md text-white shadow-2xl hover:bg-hunter-green transition-colors active:scale-95"
          style={{ touchAction: 'manipulation', boxShadow: '0 8px 32px rgba(58,90,64,0.4)' }}
        >
          <span className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.02em' }}>
            Sign In / Sign Up
          </span>
        </button>
      )}
    </div>
  );
}
